import { make } from 'vuex-pathify'
import Vue from 'vue'
import axios from '../../util/http-common'

const state = {
  companies: {},
  selectedCompanyId: 0,
  selectedCompanyName: '',
  selectedCompanyPath: [],
}

const mutations = {
  ...make.mutations(state),
  updateSelectedCompany (state, companyId) {
    const company = state.companies[companyId]

    state.selectedCompanyId = company?.id || 0
    state.selectedCompanyName = company?.name || ''
    state.selectedCompanyPath = company?.path || []
  },
  // Adds a company that isn't in the loaded tree (e.g. a sub company restored from its URL slug)
  // without attaching it to the drawer tree.
  rememberCompany (state, company) {
    if (!company?.id || state.companies[company.id]) {
      return
    }

    const name = company.name?.trim() || ''
    company.path = [{ id: company.id, name, image: company.image }]
    company.pathStr = name
    Vue.set(state.companies, company.id, company)
  },
  updateCompanies (state, { companyId, companies }) {
    function mapCompany (company) {
      return {
        id: company.id,
        name: company.name?.trim() || '',
        image: company.image,
      }
    }
    function getPath (result, company) {
      result = [mapCompany(company), ...result]

      if (!company.parentId) {
        return result
      }

      const parent = state.companies[company.parentId]
      if (!parent) {
        return result
      }

      return getPath(result, parent)
    }

    for (const company of companies) {
      company.parentId = companyId
      company.path = getPath([], company) || []
      company.pathStr = company.path?.map(x => x.name)?.join(' / ') || ''

      if (companyId !== 0) {
        const parentCompany = state.companies[companyId]
        parentCompany.children ??= []
        // Children may be loaded twice (slug lookup, then expanding the tree); replace instead of duplicating.
        const existingIndex = parentCompany.children.findIndex(child => child.id === company.id)
        if (existingIndex >= 0) {
          parentCompany.children.splice(existingIndex, 1, company)
        } else {
          parentCompany.children.push(company)
        }
      }

      Vue.set(state.companies, company.id, company)
    }
  },
}

const actions = {
  ...make.actions(state),
  selectCompany: ({ commit }, companyId) => {
    commit('updateSelectedCompany', companyId)
  },
  loadCompanies: async ({ commit }, companyId) => {
    try {
      const response = await axios.get('Company/GetSubCompaniesPagination', {
        params: {
          ...(companyId && { companyId }),
          Page: 1,
          Limit: 50,
        },
      })

      if (Array.isArray(response.data) && response.data.length > 0) {
        const companies = response.data.map((x) => {
          // eslint-disable-next-line no-prototype-builtins
          if (!x.hasOwnProperty('children')) {
            x.children = []
          }
          return x
        })

        commit('updateCompanies', { companyId, companies })
        return companies
      }
    } catch (error) {
      throw error
    }

    return []
  },
}

const getters = {
  getBaseCompanies: state => {
    return Object.values(state.companies).filter(x => x.parentId === 0)
  },
  getCompanies () {
    function mapCompany (company) {
      return {
        id: company.id,
        name: company.name?.trim() || '',
        pathStr: company.pathStr || '',
      }
    }

    return Object.values(state.companies)?.map(mapCompany)?.sort((a, b) => a.pathStr?.localeCompare(b.pathStr)) || []
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
